import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { type ApiError, type BatchCancelView, type ResolvedCatalogCategoryView, type RideGridRowView } from '@vello/api-client';
import { isIso4217Currency } from '@vello/core';
import { formatMoney } from '@vello/format';
import { AUTO_REFRESH_MS, useAutoRefresh, Badge, Button, CheckRow, EmptyState, Field, ListTable, Notice, PageHeader, Panel, Select, TextArea, TextInput, type Column, SectionTitle, Cluster, Stack, Text, FieldCell, CapWidth, FormRow, SearchInput } from '@vello/web-theme';
import {
  BATCH_MAX,
  BATCH_REASONS,
  classifySupportQuery,
  GRID_PAYMENT_METHODS,
  GRID_STATUSES,
  gridFilterChoices,
  packLabel,
  cancellableRideIds,
  customPeriod,
  formatMarketInstant,
  gridStatusTone,
  marketDate,
  marketTimeZone,
  parsePersonRidesFilter,
  pruneSelection,
  ridesReportsAuthority,
  supportQueryNeedsMarket,
  tailOf,
  toggleAllRides,
  toggleRide,
} from '../../src/index';
import { useAdmin } from '../session';
import { PersonName } from '../components/person-name';
import { RideSupportLookup } from '../components/ride-support-lookup';
import { ScopeMarketNotice, useScopeMarket } from '../components/scope-market';
import { queryString, useRouter } from '../router';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';

/**
 * Corridas › Grade (W9-RIDES-REPORTS-AUDIT, D472, ADR-0090; DevMobility A-11, C-008, I-025; X-ray A-11): the rides of ONE
 * market over a period (whole local days of the market's zone) by status, category and payment method, newest first, with a
 * "load more" that continues from the last row (a cursor, never an offset). An operator with `ride:cancel_batch` ticks the rides
 * that may still be cancelled (at most {@link BATCH_MAX}), picks ONE reason code of the closed vocabulary, and confirms in the
 * page; the server audits the batch before it touches a ride and answers one outcome per ride. A ride is only ever CANCELLED —
 * there is no delete: the history is immutable. Read only for a viewer without `ride:cancel_batch`.
 */
const DAY_MS = 86_400_000;
/** The accessible name of a checkbox that has no visible text (visually hidden, still read by a screen reader). */

export function RidesGridScreen(): ReactElement {
  const { runtime, markets, grants } = useAdmin();
  const { navigate, query: urlQuery, replaceQuery } = useRouter();
  const { t, lang } = useT();
  const { v, fail, explain } = useVocab();
  const authority = ridesReportsAuthority(grants);

  const { market, choices } = useScopeMarket();
  const zone = marketTimeZone(market, markets);

  // The default window (the last 7 local days) and the filters the operator set live in the URL (`?from=&to=&status=&category=&payment=&q=`),
  // so "Voltar" from a ride returns to this very list, filters included. A phone, an account id or a ride id typed in the search box is a
  // lookup: it never reaches the URL (D413).
  const defaultDays = useRef({ from: marketDate(new Date(Date.now() - 6 * DAY_MS), zone), to: marketDate(new Date(), zone) });
  const urlDay = (key: 'from' | 'to'): string | undefined => (/^\d{4}-\d{2}-\d{2}$/.test(urlQuery[key] ?? '') ? urlQuery[key] : undefined);
  const [fromDay, setFromDay] = useState(() => urlDay('from') ?? defaultDays.current.from);
  const [toDay, setToDay] = useState(() => urlDay('to') ?? defaultDays.current.to);
  const [status, setStatus] = useState(() => ((GRID_STATUSES as readonly string[]).includes(urlQuery.status ?? '') ? (urlQuery.status as string) : ''));
  const [category, setCategory] = useState(() => (urlQuery.category ?? '').slice(0, 40));
  const [payment, setPayment] = useState(() => (urlQuery.payment ?? '').slice(0, 40));
  const [search, setSearch] = useState(() => (urlQuery.q ?? '').slice(0, 36));
  // The support lookup (an id, an account or a phone with +country code, submitted with Enter) replaces the table while it is active.
  const [lookupQ, setLookupQ] = useState<string | undefined>(undefined);
  const [rows, setRows] = useState<readonly RideGridRowView[] | undefined>(undefined);
  const [next, setNext] = useState<string | undefined>(undefined);
  // True once "load more" appended a page: the background re-read stops so it never collapses the pages the operator opened.
  const [paged, setPaged] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [selected, setSelected] = useState<readonly string[]>([]);
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<BatchCancelView | undefined>(undefined);
  // D854 (L7a-025): the market's categories fill the "Categoria" select (the same catalogue the rider picks from).
  const [categories, setCategories] = useState<readonly ResolvedCatalogCategoryView[]>([]);
  useEffect(() => {
    if (market === undefined) {
      setCategories([]);
      return;
    }
    let live = true;
    void runtime.serviceCategories.market(market).then((res) => {
      if (live) setCategories(res.ok ? res.value.categories.filter((c) => c.status === 'ACTIVE') : []);
    });
    return () => {
      live = false;
    };
  }, [runtime, market]);
  const readSeq = useRef(0);

  const refuse = useCallback(
    (error: ApiError): string => explain(error) ?? t('rrep.err.generic', { code: fail(error) }),
    [t, fail, explain],
  );

  const period = customPeriod(fromDay, toDay, zone);

  const searchKind = classifySupportQuery(search);
  // A ride id (or an account id), a phone with +country code: answered by the support lookup. The grid's own filter takes a code or the
  // start of an identifier.
  const lookupKind = searchKind === 'ride_id' || searchKind === 'account_id_or_phone';
  const gridSearch = lookupKind || searchKind === 'national_phone' ? '' : search.trim();
  const personFilter = parsePersonRidesFilter(urlQuery);
  const lookupActive = personFilter !== undefined || lookupQ !== undefined;
  const searchHint = searchKind === 'national_phone' ? t('rides.search.hint.nationalPhone') : supportQueryNeedsMarket(searchKind) && market === undefined ? t('rides.search.hint.market') : t('rrep.filter.searchHint');

  const load = useCallback(
    async (cursor?: string): Promise<void> => {
      const seq = ++readSeq.current;
      if (lookupActive) return;
      if (market === undefined || period === undefined) {
        setRows(undefined);
        setNext(undefined);
        setError(period === undefined && market !== undefined ? t('rrep.err.period') : undefined);
        return;
      }
      const res = await runtime.ops.rideGrid({
        market,
        from: period.from,
        to: period.to,
        ...(status !== '' ? { status: [status as (typeof GRID_STATUSES)[number]] } : {}),
        ...(category.trim() !== '' ? { category: category.trim() } : {}),
        ...(payment.trim() !== '' ? { payment: payment.trim() } : {}),
        ...(gridSearch !== '' ? { q: gridSearch } : {}),
        ...(cursor !== undefined ? { cursor } : {}),
      });
      if (seq !== readSeq.current) return;
      if (res.ok) {
        setRows((prev) => (cursor !== undefined ? [...(prev ?? []), ...res.value.rides] : res.value.rides));
        setNext(res.value.nextCursor);
        setPaged(cursor !== undefined);
        setError(undefined);
      } else {
        setRows((prev) => prev ?? []);
        setError(refuse(res.error));
      }
    },
    // The period is derived from the typed days and the zone; depend on those, not on the object identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [runtime, market, lookupActive, period?.from, period?.to, status, category, payment, gridSearch, refuse, t],
  );

  // Editing the search box ends a lookup: what is on screen must be the answer to what is typed.
  useEffect(() => {
    if (lookupQ !== undefined && search.trim() !== lookupQ) setLookupQ(undefined);
  }, [lookupQ, search]);

  // The filters mirror into the URL (replace, no history entry). A typed text drops the person filter (it replaces it); an id, an account or a
  // phone is never written.
  const urlQueryRef = useRef(urlQuery);
  urlQueryRef.current = urlQuery;
  useEffect(() => {
    const out: Record<string, string> = {};
    const current = urlQueryRef.current;
    if (current.person !== undefined && current.role !== undefined && search.trim() === '') {
      out.person = current.person;
      out.role = current.role;
    }
    if (fromDay !== defaultDays.current.from) out.from = fromDay;
    if (toDay !== defaultDays.current.to) out.to = toDay;
    if (status !== '') out.status = status;
    if (category.trim() !== '') out.category = category.trim();
    if (payment.trim() !== '') out.payment = payment.trim();
    if (gridSearch !== '') out.q = gridSearch;
    if (queryString(out) !== queryString(current)) replaceQuery(out);
  }, [fromDay, toDay, status, category, payment, gridSearch, search, replaceQuery]);

  useEffect(() => {
    setRows(undefined);
    setSelected([]);
    setConfirming(false);
    void load();
  }, [load]);
  useAutoRefresh(load, { intervalMs: AUTO_REFRESH_MS.live, enabled: market !== undefined && period !== undefined && !paged && !lookupActive });

  const money = (minor: string | undefined, currency: string | undefined): string => {
    if (minor === undefined || currency === undefined) return '—';
    return isIso4217Currency(currency) ? formatMoney({ amountMinor: BigInt(minor), currency }, { locale: lang, timeZone: 'UTC', measurement: 'metric' }) : `${minor} ${currency}`;
  };
  const when = (iso: string | undefined): string => (iso === undefined ? '—' : formatMarketInstant(iso, zone, lang));

  const visible = rows ?? [];
  const ticked = pruneSelection(selected, visible);
  const allTicked = cancellableRideIds(visible).length > 0 && cancellableRideIds(visible).slice(0, BATCH_MAX).every((id) => ticked.includes(id));

  const cancelBatch = async (): Promise<void> => {
    if (market === undefined || reason === '' || ticked.length === 0) return;
    setBusy(true);
    const res = await runtime.ops.batchCancelRides({ market, rideIds: ticked, code: reason, ...(note.trim() !== '' ? { note } : {}) });
    setBusy(false);
    setConfirming(false);
    if (res.ok) {
      setResult(res.value);
      setSelected([]);
      setNote('');
      setError(undefined);
    } else {
      setResult(undefined);
      setError(refuse(res.error));
    }
    await load();
  };

  const columns: readonly Column<RideGridRowView>[] = [
    ...(authority.mayCancel
      ? [
          {
            key: 'pick',
            header: '',
            width: '56px',
            render: (r: RideGridRowView) =>
              r.cancellable ? (
                <CheckRow id={`rrep-pick-${r.rideId}`} checked={ticked.includes(r.rideId)} onChange={() => setSelected(toggleRide(ticked, r.rideId))}>
                  <span className="vello-sr-only">{t('rrep.grid.pickOne', { id: tailOf(r.rideId) })}</span>
                </CheckRow>
              ) : null,
          } satisfies Column<RideGridRowView>,
        ]
      : []),
    { key: 'ride', header: t('rrep.col.ride'), width: 'minmax(120px, 0.9fr)', render: (r) => <Button variant="ghost" onClick={() => navigate('ride_detail', { id: r.rideId })}><span className="tnum">{r.code ?? tailOf(r.rideId)}</span></Button> },
    { key: 'status', header: t('rrep.col.status'), width: 'minmax(120px, 0.8fr)', render: (r) => <Badge tone={gridStatusTone(r.status)}>{v('ride.status', r.status)}</Badge> },
    { key: 'category', header: t('rrep.col.category'), width: 'minmax(100px, 0.7fr)', render: (r) => v('ride.category', r.categoryCode) },
    { key: 'payment', header: t('rrep.col.payment'), width: 'minmax(100px, 0.7fr)', render: (r) => v('ride.payment_method', r.paymentMethod) },
    { key: 'requested', header: t('rrep.col.requested'), width: 'minmax(150px, 1fr)', render: (r) => <Text role="caption" tone="secondary" mono>{when(r.requestedAt)}</Text> },
    { key: 'driver', header: t('rrep.col.driver'), width: 'minmax(100px, 0.7fr)', render: (r) => <Text role="caption" tone="secondary"><PersonName id={r.driverId} /></Text> },
    { key: 'quoted', header: t('rrep.col.quoted'), width: 'minmax(110px, 0.8fr)', render: (r) => <span className="tnum">{money(r.quotedMinor, r.currency)}</span> },
    { key: 'final', header: t('rrep.col.final'), width: 'minmax(110px, 0.8fr)', render: (r) => <span className="tnum">{money(r.finalMinor, r.currency)}</span> },
    {
      key: 'cancel',
      header: t('rrep.col.cancel'),
      width: 'minmax(140px, 1fr)',
      render: (r) => (r.status === 'cancelled' ? <Text role="caption" tone="secondary">{r.cancelledBy !== undefined ? v('ride.actor', r.cancelledBy) : '—'}{r.cancellationReason !== undefined ? ` · ${v('ride.cancel_reason', r.cancellationReason)}` : ''}</Text> : null),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('rrep.grid.title')}
        subtitle={t('rrep.grid.subtitle')}
      />

      {!authority.mayCancel && (
        <Notice tone="neutral">{t('rrep.grid.readOnly')}</Notice>
      )}

      <Panel>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (lookupKind) setLookupQ(search.trim());
          }}
        >
        <Stack gap={4}>
          <FormRow
            columns="minmax(0, 480px)"
            action={<Button type="submit" variant="secondary" icon="search" disabled={!lookupKind}>{t('rides.search.submit')}</Button>}
          >
            <Field label={t('rrep.filter.search')} htmlFor="rrep-search" hint={searchHint}>
              <SearchInput id="rrep-search" value={search} onChange={setSearch} onClear={() => setSearch('')} placeholder={t('rides.search.placeholder')} clearLabel={t('ui.clear')} />
            </Field>
          </FormRow>
          <Cluster gap={4} wrap>
            <Field label={t('rrep.filter.from')} htmlFor="rrep-from">
              <TextInput id="rrep-from" type="date" value={fromDay} onChange={(e) => setFromDay(e.target.value)} />
            </Field>
            <Field label={t('rrep.filter.to')} htmlFor="rrep-to">
              <TextInput id="rrep-to" type="date" value={toDay} onChange={(e) => setToDay(e.target.value)} />
            </Field>
            <FieldCell size="sm">
              <Field label={t('rrep.filter.status')} htmlFor="rrep-status">
                <Select id="rrep-status" value={status} onChange={(e) => setStatus(e.target.value)}>
                  <option value="">{t('rrep.filter.allStatuses')}</option>
                  {GRID_STATUSES.map((s) => (
                    <option key={s} value={s}>{v('ride.status', s)}</option>
                  ))}
                </Select>
              </Field>
            </FieldCell>
            <FieldCell size="sm">
              <Field label={t('rrep.filter.category')} htmlFor="rrep-category">
                <Select id="rrep-category" value={category} onChange={(e) => setCategory(e.target.value)}>
                  <option value="">{t('rrep.filter.allCategories')}</option>
                  {gridFilterChoices(categories.map((c) => c.code), category).map((code) => {
                    const known = categories.find((c) => c.code === code);
                    return <option key={code} value={code}>{known !== undefined ? packLabel(known.labels, lang, code) : v('ride.category', code)}</option>;
                  })}
                </Select>
              </Field>
            </FieldCell>
            <FieldCell size="sm">
              <Field label={t('rrep.filter.payment')} htmlFor="rrep-payment">
                <Select id="rrep-payment" value={payment} onChange={(e) => setPayment(e.target.value)}>
                  <option value="">{t('rrep.filter.allPayments')}</option>
                  {gridFilterChoices(GRID_PAYMENT_METHODS, payment).map((code) => (
                    <option key={code} value={code}>{v('ride.payment_method', code)}</option>
                  ))}
                </Select>
              </Field>
            </FieldCell>
          </Cluster>
        </Stack>
        </form>
      </Panel>

      {error !== undefined && (
        <Notice tone="danger">{error}</Notice>
      )}

      {result !== undefined && (
        <Notice tone={result.summary.CANCELLED === result.results.length ? 'success' : 'warning'}>
          {t('rrep.batch.done', { cancelled: result.summary.CANCELLED, total: result.results.length, id: tailOf(result.batchId) })}
          {result.results.filter((r) => r.outcome !== 'CANCELLED').map((r) => ` · ${tailOf(r.rideId)}: ${v('ops.batch_outcome', r.outcome)}`).join('')}
        </Notice>
      )}

      {lookupActive ? (
        <RideSupportLookup lookupQ={lookupQ} market={market ?? ''} onClearLookup={() => { setSearch(''); setLookupQ(undefined); }} />
      ) : market === undefined ? (
        <Panel>
          <ScopeMarketNotice choices={choices} />
        </Panel>
      ) : (
        <>
          {authority.mayCancel && cancellableRideIds(visible).length > 0 && (
            <Button variant="ghost" onClick={() => setSelected(toggleAllRides(ticked, visible))}>{t(allTicked ? 'rrep.grid.pickNone' : 'rrep.grid.pickAll')}</Button>
          )}
          <ListTable
            columns={columns}
            rows={visible}
            rowKey={(r) => r.rideId}
            loading={rows === undefined && error === undefined}
            loadingLabel={t('ui.loading')}
            error={error !== undefined && (rows === undefined || rows.length === 0) ? t('ui.loadFailed') : undefined}
            onRetry={() => void load()}
            retryLabel={t('ui.retry')}
            empty={<EmptyState title={t('rrep.grid.empty.title')} detail={t('rrep.grid.empty.detail')} />}
            minWidth={1100}
          />
          {next !== undefined && (
            <Button variant="secondary" disabled={busy} onClick={() => void load(next)}>{t('rrep.grid.more')}</Button>
          )}

          {authority.mayCancel && ticked.length > 0 && (
            <Panel>
              <Stack gap={4}>
                <SectionTitle>{t('rrep.batch.title', { count: ticked.length })}</SectionTitle>
                <Text as="p" role="caption" tone="secondary">{t('rrep.batch.explain', { max: BATCH_MAX })}</Text>
                <CapWidth size="lg"><Stack gap={3}>
                  <Field label={t('rrep.batch.reason')} htmlFor="rrep-reason">
                    <Select id="rrep-reason" value={reason} onChange={(e) => setReason(e.target.value)}>
                      <option value="">{t('rrep.batch.choose')}</option>
                      {BATCH_REASONS.map((c) => (
                        <option key={c} value={c}>{v('ops.cancel_reason', c)}</option>
                      ))}
                    </Select>
                  </Field>
                  <Field label={t('rrep.batch.note')} htmlFor="rrep-note" hint={t('rrep.batch.noteHint')}>
                    <TextArea id="rrep-note" value={note} maxLength={400} onChange={(e) => setNote(e.target.value)} />
                  </Field>
                  {confirming ? (
                    <Notice tone="warning">{t('rrep.batch.confirm', { count: ticked.length })}</Notice>
                  ) : null}
                  <Cluster gap={3}>
                    {confirming ? (
                      <Button variant="primary" disabled={busy || reason === ''} onClick={() => void cancelBatch()}>{t('rrep.batch.confirmAction')}</Button>
                    ) : (
                      <Button variant="primary" disabled={busy || reason === ''} onClick={() => setConfirming(true)}>{t('rrep.batch.action')}</Button>
                    )}
                    <Button variant="ghost" disabled={busy} onClick={() => (confirming ? setConfirming(false) : setSelected([]))}>{t('ui.cancel')}</Button>
                  </Cluster>
                </Stack></CapWidth>
              </Stack>
            </Panel>
          )}
        </>
      )}
    </>
  );
}
